/**
 * Settings → Notifications helpers (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md, WS-C).
 */

export type TargetKind = 'admin' | 'email' | 'slack_channel' | 'super_admins';

export interface NotificationRoute {
  id: string;
  event_key: string;
  target_kind: TargetKind;
  target: string;
  area: string | null;
  min_severity: string | null;
  enabled: boolean;
  admin_email: string | null;
  admin_name: string | null;
}

export interface Delivery { to_email: string; event: string; at: string | null }

export interface NotificationSend {
  id: string;
  subject: string;
  recipients: string[];
  slack_channels: string[];
  basis: 'routes' | 'defaults';
  status: 'sent' | 'failed' | 'no_recipients';
  error: string | null;
  created_at: string | null;
  delivery: Delivery[];
}

export interface NotificationEventInfo {
  key: string;
  label: string;
  description: string;
  required: boolean;
  alert_routing: boolean;
  source: string;
  /** Security events and P0 alerts: only a super admin may change who receives them. */
  protected?: boolean;
  default_targets: { target_kind: TargetKind; target: string }[];
  default_min_severity: string | null;
  routes: NotificationRoute[];
  basis: 'routes' | 'defaults';
  effective_emails: string[];
  effective_slack_channels: string[];
  recent: NotificationSend[];
}

export interface NotificationsResponse {
  events: NotificationEventInfo[];
  sender: { address: string; name: string; reply_to?: string };
  areas: { key: string; label: string }[];
  target_kinds: TargetKind[];
}

/** How one recipient reads on the page. */
export function targetLabel(kind: TargetKind, target: string, adminEmail?: string | null, adminName?: string | null): string {
  if (kind === 'super_admins') return 'Every super admin';
  if (kind === 'admin') return adminName || adminEmail || 'A team member (inactive)';
  if (kind === 'slack_channel') return target.startsWith('#') ? `Slack ${target}` : `Slack channel ${target}`;
  return target;
}

/** "Data & devices · warning or worse" for alert routes; '' otherwise. */
export function filterLabel(area: string | null, minSeverity: string | null,
  areas: { key: string; label: string }[]): string {
  const parts: string[] = [];
  if (area) parts.push(areas.find(a => a.key === area)?.label ?? area);
  if (minSeverity) parts.push(minSeverity === 'info' ? 'any severity' : `${minSeverity} or worse`);
  return parts.join(' · ');
}

/**
 * What the delivery column says for one recipient of a send. SendGrid's
 * latest event per address, else what our own send knows.
 */
export function deliveryLabel(send: NotificationSend, email: string): string {
  const d = send.delivery.find(x => x.to_email?.toLowerCase() === email.toLowerCase());
  if (d) {
    const map: Record<string, string> = {
      delivered: 'Delivered', open: 'Opened', click: 'Opened', bounce: 'Bounced', dropped: 'Dropped',
      deferred: 'Delayed', spamreport: 'Marked as spam', processed: 'Accepted by SendGrid',
    };
    return map[d.event] ?? d.event;
  }
  if (send.status === 'sent') return 'Sent (no delivery report yet)';
  if (send.status === 'no_recipients') return 'Nobody to send to';
  return 'Not sent';
}

/** Client-side check before POST /routes (the server validates again). */
export function validateTarget(kind: TargetKind, raw: string): string | null {
  const v = raw.trim();
  if (!v) return 'Enter a recipient.';
  if (kind === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return "That isn't a valid email address.";
  if (kind === 'slack_channel' && !/^(#[a-z0-9][a-z0-9._-]{0,79}|[CG][A-Z0-9]{6,20})$/.test(v)) {
    return 'Use #channel-name (lowercase) or a channel id like C0123ABCD.';
  }
  return null;
}

/** May this admin change who receives this notification? */
export function canEditEvent(ev: Pick<NotificationEventInfo, 'protected'>, canWrite: boolean, role?: string | null): boolean {
  return canWrite && (!ev.protected || role === 'super_admin');
}
